import PageHeading from '@/components/common/PageHeading';
// ProfessionalLoadPage.jsx
//
// Admin page listing professionals that exist in the database but were absent
// from the most recent Excel import. The importer no longer deletes them
// automatically, so removal is an explicit, reviewed action taken here.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'react-bootstrap';
import { adminCss, modalCss, buttonCss } from '@digital_monorepo/styling';
import { getProfessionalsMissingFromImport, bulkDeleteProfessionals } from '@/services/schedulingService';

const ProfessionalLoadPage = () => {
  const { t } = useTranslation();

  const [professionals, setProfessionals] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const selectAllRef = useRef(null);

  useEffect(() => {
    const fetchMissing = async () => {
      try {
        const data = await getProfessionalsMissingFromImport();
        setProfessionals(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching professionals missing from import:', err);
        setError(t('PROFESSIONAL_LOAD_TABLE.LOAD_ERROR'));
      } finally {
        setLoading(false);
      }
    };
    fetchMissing();
  }, [t]);

  const allSelected = professionals.length > 0 && selected.size === professionals.length;
  const someSelected = selected.size > 0 && !allSelected;

  // `indeterminate` is a DOM property with no JSX attribute equivalent.
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const selectedNames = useMemo(() => professionals.filter(p => selected.has(p.ProfessionalID)), [professionals, selected]);

  const toggleRow = id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(prev => (prev.size === professionals.length ? new Set() : new Set(professionals.map(p => p.ProfessionalID))));
  };

  const handleDelete = async () => {
    const ids = [...selected];
    setDeleting(true);
    try {
      await bulkDeleteProfessionals(ids);
      setProfessionals(prev => prev.filter(p => !selected.has(p.ProfessionalID)));
      setSelected(new Set());
      setShowConfirm(false);
    } catch (err) {
      console.error('Error deleting professionals:', err);
      setError(t('PROFESSIONAL_LOAD_TABLE.DELETE_ERROR'));
      setShowConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = value => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  if (loading) {
    return (
      <div className={adminCss.adminContainer}>
        <div className='text-center py-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='workspace-management-page'>
      <PageHeading title='Professional import' />
      <div className={adminCss.adminContainer}>
        <h2>{t('PROFESSIONAL_LOAD_TABLE.TITLE')}</h2>
        <p>{t('PROFESSIONAL_LOAD_TABLE.DESCRIPTION')}</p>

        {error && (
          <div className='alert alert-danger' role='alert'>
            {error}
          </div>
        )}

        {professionals.length === 0 ? (
          <div className={adminCss.noData}>{t('PROFESSIONAL_LOAD_TABLE.EMPTY')}</div>
        ) : (
          <>
            <div className={adminCss.actionButtons}>
              <Button variant='danger' onClick={() => setShowConfirm(true)} disabled={selected.size === 0} className={`${buttonCss.primaryBtn} ${adminCss.adminButton}`}>
                {t('PROFESSIONAL_LOAD_TABLE.DELETE_SELECTED', { selectedCount: selected.size })}
              </Button>
            </div>

            <div className={adminCss.tableResponsive}>
              <table className='table'>
                <thead>
                  <tr>
                    <th>
                      <input type='checkbox' ref={selectAllRef} checked={allSelected} onChange={toggleAll} aria-label={t('PROFESSIONAL_LOAD_TABLE.SELECT_ALL')} />
                    </th>
                    <th>{t('PROFESSIONAL_LOAD_TABLE.HEADERS.PROFESSIONAL_ID')}</th>
                    <th>{t('PROFESSIONAL_LOAD_TABLE.HEADERS.PROFESSIONAL_NAME')}</th>
                    <th>{t('PROFESSIONAL_LOAD_TABLE.HEADERS.FUNCTION_LEVEL')}</th>
                    <th>{t('PROFESSIONAL_LOAD_TABLE.HEADERS.OFFICE')}</th>
                    <th>{t('PROFESSIONAL_LOAD_TABLE.HEADERS.AREA')}</th>
                    <th>{t('PROFESSIONAL_LOAD_TABLE.HEADERS.LAST_SEEN_IMPORT')}</th>
                  </tr>
                </thead>
                <tbody>
                  {professionals.map(professional => (
                    <tr key={professional.ProfessionalID}>
                      <td>
                        <input type='checkbox' checked={selected.has(professional.ProfessionalID)} onChange={() => toggleRow(professional.ProfessionalID)} aria-label={professional.ProfessionalName} />
                      </td>
                      <td>{professional.ProfessionalID}</td>
                      <td>{professional.ProfessionalName}</td>
                      <td>{professional.FunctionLevel}</td>
                      <td>{professional.Office}</td>
                      <td>{professional.area}</td>
                      <td>{formatDate(professional.LastSeenImport)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered dialogClassName={adminCss.centeredModal} className={`${modalCss['modal-overlay']}`}>
        <div className={modalCss['modal-content']}>
          <Modal.Header closeButton>
            <Modal.Title>{t('PROFESSIONAL_LOAD_TABLE.CONFIRM_TITLE')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t('PROFESSIONAL_LOAD_TABLE.CONFIRM_BODY', { selectedCount: selected.size })}</p>
            <ul>
              {selectedNames.map(professional => (
                <li key={professional.ProfessionalID}>
                  {professional.ProfessionalName} ({professional.ProfessionalID})
                </li>
              ))}
            </ul>
            <p className='text-danger mt-2'>{t('PROFESSIONAL_LOAD_TABLE.CONFIRM_WARNING')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowConfirm(false)} disabled={deleting} className={`${buttonCss.secondaryBtn} ${adminCss.adminButton}`}>
              {t('PROFESSIONAL_LOAD_TABLE.CANCEL')}
            </Button>
            <Button variant='danger' onClick={handleDelete} disabled={deleting} className={`${buttonCss.primaryBtn} ${adminCss.adminButton}`}>
              {t('PROFESSIONAL_LOAD_TABLE.DELETE')}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default ProfessionalLoadPage;
